import { Injectable } from "@angular/core";

import { combineLatest, Observable, of, throwError } from "rxjs";
import { catchError, filter, map, mapTo, shareReplay, switchMap, tap } from "rxjs/operators";

import { dbToFloat, DffError, getErrorMessage, parseDate } from "@dffedb/util";
import { AuthenticationService, LoginService } from "@e-forsyning/common/auth";
import { BrugerRepository } from "@e-forsyning/common/bruger";
import { AppserverInstallationCredentialsRepository } from "@e-forsyning/common/credentials";
import { InstallationHttpClient, ParametreForAktuelleInstallation, UmbracoHttpClient } from "@e-forsyning/common/http";
import { LogLevel, LogService } from "@e-forsyning/common/log";
import { RedirectService } from "@e-forsyning/common/router";

import { ForbrugDto } from "./forbrug.dto";
import { Forbrug } from "./forbrug.model";
import { MitForbrugIndstillinger } from "./mit-forbrug-indstillinger.model";
import { MitForbrugIndstillingerService } from "./mit-forbrug-indstillinger.service";
import { ForbrugParams, ForbrugRequestDto, MitForbrugViewModel } from "./mit-forbrug.view-model";

@Injectable({ providedIn: "root" })
export class NyMitForbrugService {
    private forbrugsArray = [];
    private _forbrugCache = new Map<string, Observable<[ForbrugRequestDto, ForbrugDto]>>();

    constructor(
        private readonly umbracoHttpClient: UmbracoHttpClient,
        private readonly installationHttpClient: InstallationHttpClient,
        private readonly credentialsRepository: AppserverInstallationCredentialsRepository,
        private readonly mitForbrugIndstillingerService: MitForbrugIndstillingerService,
        private readonly log: LogService,
        private readonly authenticationService: AuthenticationService,
        private readonly loginService: LoginService,
        private readonly redirectService: RedirectService,
        private readonly brugerRepository: BrugerRepository
    ) {}

    public hentIndstillinger(): Observable<MitForbrugIndstillinger> {
        return this.mitForbrugIndstillingerService.hentIndstillinger();
    }

    public getAktuelAarsmaerke(): Observable<any> {
        return this.installationHttpClient.get("api/getaktuelaarsmaerke");
    }

    public getYearsForbrug(data1: any, data2: any, data3: any, data4: any, data5: any): Observable<any> {
        this.forbrugsArray = [];
        const result1 = this.installationHttpClient.post("api/getforbrug", data1, ParametreForAktuelleInstallation.true).pipe(
            catchError((e) => {
                throw new DffError("Forbrugsdata kunne ikke hentes for " + data1.AarsMaerke);
            })
        );
        const result2 = this.installationHttpClient.post("api/getforbrug", data2, ParametreForAktuelleInstallation.true).pipe(
            catchError((e) => {
                throw new DffError("Forbrugsdata kunne ikke hentes for " + data2.AarsMaerke);
            })
        );
        const result3 = this.installationHttpClient.post("api/getforbrug", data3, ParametreForAktuelleInstallation.true).pipe(
            catchError((e) => {
                throw new DffError("Forbrugsdata kunne ikke hentes for " + data3.AarsMaerke);
            })
        );
        const result4 = this.installationHttpClient.post("api/getforbrug", data4, ParametreForAktuelleInstallation.true).pipe(
            catchError((e) => {
                throw new DffError("Forbrugsdata kunne ikke hentes for " + data4.AarsMaerke);
            })
        );
        const result5 = this.installationHttpClient.post("api/getforbrug", data5, ParametreForAktuelleInstallation.true).pipe(
            catchError((e) => {
                throw new DffError("Forbrugsdata kunne ikke hentes for " + data5.AarsMaerke);
            })
        );

        return combineLatest([result1, result2, result3, result4, result5]).pipe(
            map(([res1, res2, res3, res4, res5]) => {
                this.forbrugsArray.push(res1);
                this.forbrugsArray.push(res2);
                this.forbrugsArray.push(res3);
                this.forbrugsArray.push(res4);
                this.forbrugsArray.push(res5);
            })
        );
    }

    public getAflaesFile(vm: MitForbrugViewModel): Observable<any> {
        return this.installationHttpClient.getWithStringResult("api/getaflaestilregneark", ParametreForAktuelleInstallation.true).pipe(
            switchMap((result) => {
                if (result) {
                    const csvData = result;
                    const filename = "Aflaesninger.csv";
                    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
                    const isChrome = navigator.userAgent.toLowerCase().indexOf("chrome") > -1;
                    const isSafari = navigator.userAgent.toLowerCase().indexOf("safari") > -1;

                    if (isIOS && isSafari) {
                        const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + encodeURIComponent(csvData);

                        // Create a temporary link element
                        const link = document.createElement("a");
                        link.href = csvContent;
                        link.download = filename;

                        // Append the link to the body and trigger the click
                        document.body.appendChild(link);
                        link.click();

                        // Remove the link after the click
                        document.body.removeChild(link);
                    } else if (!vm.isMobile) {
                        if ((navigator as any).msSaveBlob) {
                            (navigator as any).msSaveBlob(new Blob(["\uFEFF" + csvData], { type: "text/csv;charset=utf-8;" }), filename);
                        } else if (isChrome) {
                            const pom = document.createElement("a");
                            pom.setAttribute("href", "data:text/csv;charset=utf-8,\uFEFF" + encodeURIComponent(csvData));
                            pom.setAttribute("download", filename);
                            pom.click();
                        } else if (isSafari) {
                            const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + encodeURIComponent(csvData);
                            window.open(csvContent, "_blank");
                        } else {
                            const f = document.createElement("iframe");
                            f.style.display = "none";
                            f.src = "data:text/csv;charset=utf-8,\uFEFF" + encodeURIComponent(csvData);
                            document.body.appendChild(f);
                            setTimeout(() => document.body.removeChild(f), 1000);
                        }
                    } else {
                        if (isIOS) {
                            const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + encodeURIComponent(csvData);
                            window.open(csvContent, "_blank");
                        } else {
                            const blob = new Blob(["\uFEFF" + csvData], { type: "text/csv;charset=utf-8;" });
                            const url = window.URL.createObjectURL(blob);

                            const link = document.createElement("a");
                            link.href = url;
                            link.download = filename;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);

                            window.URL.revokeObjectURL(url);
                        }
                    }

                    return result;
                } else {
                    throw new DffError("Funktionen er ikke tilgængelig i øjeblikket - Prøv igen senere.");
                }
            })
        );
    }

    public getForbrug(params: ForbrugParams): Observable<Forbrug> {
        return this.checkAuthenticated().pipe(
            filter((authenticated) => authenticated),
            switchMap(() => this.hentDefaultForbrugRequestDto()),
            switchMap((defaultParams) => this.getForbrugCached({ ...defaultParams, ...params }))
        );

        //        return this.hentDefaultForbrugRequestDto().pipe(
        //            switchMap((defaultParams) => this.getForbrugCached({ ...defaultParams, ...params }))
        //        );
    }

    private checkAuthenticated(): Observable<boolean> {
        if (!this.authenticationService.isAuthenticationTimeout()) {
            return of(true);
        }
        return this.erBrugerLoggetInd().pipe(
            switchMap((erLoggetInd) => {
                if (erLoggetInd) {
                    return of(true);
                }

                return this.loginService.loginMedGemteCredentials().pipe(
                    map((result) => result.success),
                    catchError(() => of(false))
                );
            }),
            tap((result) => {
                if (!result) {
                    this.redirectService.redirectTilLogin(true);
                }
            })
        );
    }

    /**
     * Undersøger om vi har valide credentials for den aktuelle bruger.
     * Hvis der er gået noget tid uden aktivitet, kan credentials være timet ud.
     */
    private erBrugerLoggetInd(): Observable<boolean> {
        // Vi bruger repository direkte for at kalde undgå cache, da vi ønsker at tjekke om serveren stadig kender os.
        return this.brugerRepository.hentAktuelleBruger().pipe(
            mapTo(true),
            catchError((error) => {
                console.warn("canActivate: " + getErrorMessage(error));
                return of(false);
            })
        );
    }

    private getForbrugCached(requestDto: ForbrugRequestDto): Observable<Forbrug> {
        const key = JSON.stringify({ ...requestDto, aCallKey: 0 });
        const result =
            this._forbrugCache.get(key) ||
            this.installationHttpClient.post<ForbrugDto>("api/getforbrug", requestDto, ParametreForAktuelleInstallation.true).pipe(
                switchMap((data) => (data && of(data)) || throwError(new DffError("Intet forbrugsdata fundet"))),
                map((data) => [requestDto, data] as [ForbrugRequestDto, ForbrugDto]),
                shareReplay(1)
            );
        this._forbrugCache.set(key, result);
        return result.pipe(map((r) => this.mapForbrug(r)));
    }

    private mapForbrug([requestDto, dto]: [ForbrugRequestDto, ForbrugDto]): Forbrug {
        this.fixIaltLinjeAflaesningSlutMedForkertVaerdi(dto);
        this.fixIaltLinjeFraDatoStr1899(requestDto, dto);

        const result: Forbrug = {
            aarsmaerke: requestDto.AarsMaerke,
            aar: {
                start: parseDate(dto.AarStart, "DD-MM-YYYY"),
                slut: parseDate(dto.AarSlut, "DD-MM-YYYY")
            },
            forbrugsLinjer: (dto.ForbrugsLinjer.TForbrugsLinje || []).map((l) => ({
                beregnet: l.Fiktiv === "True",
                periode: {
                    start: parseDate(l.FraDatoStr, "DD-MM-YYYY"),
                    slut: parseDate(l.TilDatoStr, "DD-MM-YYYY")
                },
                dato: {
                    fra: parseDate(l.FraDatoStr, "DD-MM-YYYY"),
                    til: parseDate(l.TilDatoStr, "DD-MM-YYYY")
                },
                ekskluderetMedFilter: l.EkskluderetMedFilter === "True",
                forventetAflaesning: {
                    eng1: dbToFloat(l.ForventetAflaesningENG1),
                    m3: dbToFloat(l.ForventetAflaesningM3)
                },
                forventetForbrug: {
                    eng1: dbToFloat(l.ForventetForbrugENG1),
                    m3: dbToFloat(l.ForventetForbrugM3)
                },
                taellevaerker: (l.TForbrugsTaellevaerk || []).map((tv) => ({
                    indexNavn: tv.IndexNavn,
                    enhedsTekst: tv.Enhed_Txt,
                    aflaesning: {
                        start: dbToFloat(tv.Start),
                        slut: dbToFloat(tv.Slut)
                    },
                    forbrug: dbToFloat(tv.Forbrug)
                })),
                temperatur: {
                    afkoeling: dbToFloat(l.Afkoling),
                    forventeRetur: dbToFloat(l.Forv_Retur),
                    frem: dbToFloat(l.Tempfrem),
                    retur: dbToFloat(l.TempRetur),
                    neutraltOmraade: {
                        nedre: dbToFloat(l.NeutraltOmraadeNedre),
                        oevre: dbToFloat(l.NeutraltOmraadeOvre)
                    }
                }
            })),
            ialtLinje: {
                beregnet: false,
                periode: {
                    start: parseDate(dto.IaltLinje.FraDatoStr, "DD-MM-YYYY"),
                    slut: parseDate(dto.IaltLinje.TilDatoStr, "DD-MM-YYYY")
                },
                ekskluderetMedFilter: false,
                forventetAflaesning: {
                    eng1: null,
                    m3: null
                },
                forventetForbrug: {
                    eng1: dbToFloat(dto.IaltLinje.ForventetForbrugENG1),
                    m3: dbToFloat(dto.IaltLinje.ForventetForbrugM3)
                },
                temperatur: {
                    afkoeling: dbToFloat(dto.IaltLinje.Afkoling),
                    forventeRetur: dbToFloat(dto.IaltLinje.Forv_Retur),
                    frem: dbToFloat(dto.IaltLinje.Tempfrem),
                    retur: dbToFloat(dto.IaltLinje.TempRetur),
                    neutraltOmraade: {
                        nedre: dbToFloat(dto.IaltLinje.NeutraltOmraadeNedre),
                        oevre: dbToFloat(dto.IaltLinje.NeutraltOmraadeOvre)
                    }
                },
                taellevaerker: (dto.IaltLinje.TForbrugsTaellevaerk || []).map((tv) => ({
                    indexNavn: tv.IndexNavn,
                    enhedsTekst: tv.Enhed_Txt,
                    aflaesning: {
                        start: dbToFloat(tv.Start),
                        slut: dbToFloat(tv.Slut)
                    },
                    forbrug: dbToFloat(tv.Forbrug)
                }))
            },
            taellevaerker: dto.IaltLinje.Taellevaerker.Text.split("|")
        };
        return result;
    }

    private fixIaltLinjeFraDatoStr1899(requestDto: ForbrugRequestDto, dto: ForbrugDto): void {
        if (dto.IaltLinje.FraDatoStr.endsWith("1899")) {
            this.log.log({
                /* eslint-disable @typescript-eslint/naming-convention */
                LogLevel: LogLevel.Warning,
                Besked: `Mit forbrug: IaltLinje med FraDato i 1899 (${dto.IaltLinje.FraDatoStr} - ${dto.IaltLinje.TilDatoStr})`,
                Detaljer: `REQUEST:${JSON.stringify(requestDto)} RESPONSE:${JSON.stringify(dto)}`
                /* eslint-enable @typescript-eslint/naming-convention */
            });
            dto.IaltLinje.FraDatoStr = `01-01-${requestDto.AarsMaerke}`;
            dto.IaltLinje.TilDatoStr = `31-12-${requestDto.AarsMaerke}`;
        }
    }

    private fixIaltLinjeAflaesningSlutMedForkertVaerdi(dto: ForbrugDto): void {
        // Fix: Forkert måleraflæsning på ialtlinjen.
        // Vi erstatter derfor med måleraflæsning på den sidste forbrugslinje.
        if ((dto.ForbrugsLinjer.TForbrugsLinje || []).length === 0) {
            return;
        }
        const sidsteLinje = dto.ForbrugsLinjer.TForbrugsLinje[dto.ForbrugsLinjer.TForbrugsLinje.length - 1].TForbrugsTaellevaerk;
        for (const tv of dto.IaltLinje.TForbrugsTaellevaerk) {
            const tvSidsteLinje = sidsteLinje.find((x) => x.IndexNavn === tv.IndexNavn);
            if (tvSidsteLinje) {
                tv.Slut = tvSidsteLinje.Slut;
            }
        }
    }

    private hentDefaultForbrugRequestDto(): Observable<ForbrugRequestDto> {
        return this.credentialsRepository.valueChanges.pipe(
            map((credentials) => ({
                aCallKey: 0,
                /* eslint-disable @typescript-eslint/naming-convention */
                ForbrugsAfgraensning_FraAflaesning: "0",
                ForbrugsAfgraensning_TilAflaesning: "2",
                Ejendomnr: credentials.ejendomNr,
                ForbrugsAfgraensning_FraMellNr: "0",
                ForbrugsAfgraensning_TilJournalNr: "0",
                BestemtEnhed: "0",
                Godkendelser: "0",
                RadEksponent2: "0",
                RadEksponent1: "0",
                AarsMaerke: 0,
                ForbrugsAfgraensning_TilDato: "0",
                Optioner: "foSkabDetaljer, foBestemtBeboer, foMedtagWebAflaes",
                ForbrugsAfgraensning_TilAfl_nr: "0",
                Belastningsfaktor: "0",
                ForbrugsAfgraensning_FraDato: "0",
                AHoejDetail: false,
                AktivNr: credentials.aktivNr,
                ForbrugsAfgraensning_FraAfl_nr: "0",
                I_Nr: credentials.installationNr,
                ForbrugsAfgraensning_TilMellnr: "0",
                Aflaesningsfilter: "afMaanedsvis",
                ForbrugsAfgraensning_MedtagMellemliggendeMellemaflas: true,
                SletFiltreredeAflaesninger: true,
                AflaesningsUdjaevning: true,
                MedForventetForbrug: true,
                AflaesningsFilterDag: "ULTIMO",
                OmregnForbrugTilAktuelleEnhed: true
                /* eslint-enable @typescript-eslint/naming-convention */
            }))
        );
    }
}

export function substring(subject: string, indexA: number, indexB: number): string {
    return subject === undefined || subject === "" ? "" : subject.substring(indexA, indexB);
}
